import { motion } from 'motion/react'
import { useRef } from 'react'
import blueconicLogo from '@/assets/onboarding/blueconic-logo.svg'
import { onboarding } from '@/lib/motion/presets'
import { useWelcomeTransition } from '@/stores/welcomeTransition'
import type { WelcomeTransitionPhase } from '@/stores/welcomeTransition.types'

export function WelcomeTransitionOverlay() {
  const phase = useWelcomeTransition((state) => state.phase)
  const markCovered = useWelcomeTransition((state) => state.markCovered)
  const reset = useWelcomeTransition((state) => state.reset)
  const completedPanels = useRef(new Set<'left' | 'right'>())
  const completedPhase = useRef<WelcomeTransitionPhase | null>(null)

  if (phase === 'idle') return null

  const handlePanelComplete = (panel: 'left' | 'right') => {
    if (completedPhase.current !== phase) {
      completedPanels.current.clear()
      completedPhase.current = phase
    }

    completedPanels.current.add(panel)

    if (completedPanels.current.size !== 2) return

    if (phase === 'covering') markCovered()
    if (phase === 'revealing') reset()
  }

  const isRevealing = phase === 'revealing'

  return (
    <div
      data-testid="welcome-transition-overlay"
      className="pointer-events-auto fixed inset-0 z-[110] overflow-hidden"
    >
      <motion.div
        aria-hidden="true"
        data-testid="welcome-panel-left"
        initial={{ x: '-100%' }}
        animate={{ x: isRevealing ? '-100%' : '0%' }}
        transition={
          isRevealing ? onboarding.welcome.reveal : onboarding.welcome.cover
        }
        onAnimationComplete={() => handlePanelComplete('left')}
        className="absolute inset-y-0 left-0 w-1/2 bg-[#0067A0]"
      />
      <motion.div
        aria-hidden="true"
        data-testid="welcome-panel-right"
        initial={{ x: '100%' }}
        animate={{ x: isRevealing ? '100%' : '0%' }}
        transition={
          isRevealing ? onboarding.welcome.reveal : onboarding.welcome.cover
        }
        onAnimationComplete={() => handlePanelComplete('right')}
        className="absolute inset-y-0 right-0 w-1/2 bg-[#0067A0]"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isRevealing ? 0 : 1 }}
        transition={onboarding.welcome.caption}
        className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-white"
      >
        <div className="rounded-xl bg-white p-3">
          <img src={blueconicLogo} alt="BlueConic" className="h-12 w-12" />
        </div>
        <output aria-atomic="true" className="text-xl font-medium">
          Welcome to Vwam
        </output>
      </motion.div>
    </div>
  )
}
